import { Filter as FilterIcon } from "lucide-react";
import * as React from "react";

import { Button } from "@/shared/foundation/primitives/Button";
import { usePopupState } from "@/shared/hooks/usePopupState";

import type { FilterDefinition } from "./FilterDefinition";
import { FilterMenu } from "./FilterMenu";
import styles from "./Filters.module.css";
import type { Filter } from "./FilterSchema";

export interface FiltersProps {
  /**
   * Filterable fields available in the picker. The component does not fetch
   * schema or data itself — the consumer is responsible for assembling this
   * list (typically a static set for built-in fields plus a dynamic set for
   * org-specific custom fields).
   */
  filterDefinitions: FilterDefinition[];

  /**
   * Fired when the user completes a single filter configuration.
   */
  onFilterSelected?: (filter: Filter) => void;
}

/**
 * Labeled "Filter" outlined button in the Actions row. Opens the shared
 * `<FilterMenu>` popover (also reached via the inline `+` button in
 * `<ResourceView.ActiveFilters>`); the menu lets the user enter exactly
 * one filter at a time and emits it via `onFilterSelected`.
 */
export function Filters({ filterDefinitions, onFilterSelected }: FiltersProps) {
  const popup = usePopupState();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (popup.isOpen) {
      popup.close();
    } else {
      popup.open({ anchorEl: event.currentTarget });
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={
          <FilterIcon className={styles.triggerIcon} aria-hidden="true" />
        }
        className={styles.trigger}
        onClick={handleClick}
        aria-haspopup="dialog"
        aria-expanded={popup.isOpen}
      >
        <span className={styles.triggerLabel}>Filter</span>
      </Button>
      <FilterMenu
        open={popup.isOpen}
        anchorEl={popup.anchor}
        onClose={popup.close}
        filterDefinitions={filterDefinitions}
        onCommit={(filter) => onFilterSelected?.(filter)}
      />
    </>
  );
}
